import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, BarChart3, Award } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ab08fc0-7b0a-4d63-87b4-9e424b7b0b45/artifacts/9owdzxiw_ED%20Transp%20footer%20Logo.png";

export default function Home() {
  const services = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Influencer Management",
      description: "Full-service management for influencers and digital creators. We handle career growth, negotiations, and strategic planning."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Social Media Management",
      description: "Strategic social media planning, content calendars, and analytics-driven growth across all platforms."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background z-10" />
        
        {/* Subtle Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/5 rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-20 container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Logo */}
            <motion.img
              src={LOGO_URL}
              alt="El Diablo"
              className="h-28 md:h-36 lg:h-44 mx-auto mb-8 hero-logo-glow"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
            
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-text-primary mb-6 leading-tight">
              Creative Talent
              <span className="block text-primary text-glow-red">Management</span>
            </h1>
            
            <p className="text-text-secondary text-base md:text-lg max-w-2xl mx-auto mb-10">
              A boutique agency specializing in influencer management and social media strategy. 
              We build careers and drive results.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary flex items-center gap-2 justify-center"
                >
                  Work With Us
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary flex items-center gap-2 justify-center"
                >
                  Our Services
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              What We <span className="text-primary text-glow-red">Do</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Expert influencer management and social media solutions for creators.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass p-8 hover:border-primary/50 transition-colors"
              >
                <div className="text-primary mb-4">{service.icon}</div>
                <h3 className="font-heading text-xl text-text-primary mb-3">
                  {service.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary inline-flex items-center gap-2"
              >
                View All Services
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-surface/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
                Why <span className="text-primary text-glow-red">El Diablo</span>
              </h2>
              <p className="text-text-secondary mb-6">
                We take a selective approach to representation, focusing on quality 
                and sustainable career growth. Our team brings deep 
                industry expertise across the digital landscape.
              </p>
              <ul className="space-y-4">
                {[
                  "Personalized career development strategies",
                  "Expert social media growth tactics",
                  "Transparent contracts and fair terms",
                  "Dedicated support for every project"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Award className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-text-secondary">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass p-8"
            >
              <h3 className="font-heading text-xl text-text-primary mb-4">Our Approach</h3>
              <p className="text-text-secondary text-sm mb-6">
                We believe in building lasting relationships, not quick transactions. 
                Every creator we represent receives personalized attention and 
                strategic guidance tailored to their unique goals.
              </p>
              <Link to="/about">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary w-full"
                >
                  Learn More About Us
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
              Ready to Elevate Your Career?
            </h2>
            <p className="text-text-secondary mb-8">
              Whether you're an established creator or just starting out, 
              we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/careers">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Join Our Roster
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  Contact Us
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
